import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Warning, X, ArrowRight } from "@phosphor-icons/react";
import { usePlanFlags } from "@/contexts/PlanFlagsContext";

/**
 * Global amber (or red, when expired) strip pinned above the app content.
 *
 * Rendering rules:
 *   • Owner/Admin only — the provider drops `plan` for other roles.
 *   • Only when `plan.billing_cycle === "trial"` (or the plan already expired).
 *   • Last 7 days of trial → amber "expiring" copy.
 *   • After expiry → red "expired" copy (never auto-dismissed).
 *
 * Users can dismiss the *expiring* variant for the current tab session; the
 * expired variant stays until they upgrade — that's the whole point of the
 * SaaS conversion pressure.
 */
const SESSION_KEY = "trial_banner_dismissed_v1";

export default function TrialBanner() {
  const { plan, meta } = usePlanFlags();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(
    () => (typeof window !== "undefined" && sessionStorage.getItem(SESSION_KEY) === "1")
  );

  if (!plan || !meta) return null;

  const daysLeft = meta.days_remaining;
  const cycle = plan.billing_cycle;
  const isTrial = cycle === "trial";
  const isExpired = typeof daysLeft === "number" && daysLeft < 0;
  const isExpiringSoon = typeof daysLeft === "number" && daysLeft >= 0 && daysLeft <= 7;

  // Only surface for trial plans that are near/over the wire, or any plan
  // that has already lapsed.
  if (!isExpired && !(isTrial && isExpiringSoon)) return null;
  if (isExpiringSoon && !isExpired && dismissed) return null;

  const onDismiss = () => {
    sessionStorage.setItem(SESSION_KEY, "1");
    setDismissed(true);
  };

  const tone = isExpired
    ? "bg-red-50 border-red-200 text-red-900"
    : "bg-amber-50 border-amber-200 text-amber-900";

  // iter-67k (Area 5) — label distinguishes trial vs paid plans so an
  // expired Professional plan doesn't say "trial expired".
  const noun = isTrial ? "trial" : "plan";
  const verb = isTrial ? "Upgrade" : "Renew";

  const label = isExpired
    ? <>Your {noun} <span className="font-semibold">expired {Math.abs(daysLeft)} day{Math.abs(daysLeft) !== 1 ? "s" : ""} ago</span>. {verb} to keep using {plan.tier_name}.</>
    : <>Your trial expires in <span className="font-semibold">{daysLeft} day{daysLeft !== 1 ? "s" : ""}</span>. Upgrade now to avoid disruption.</>;

  return (
    <div
      role="alert"
      data-testid="trial-banner"
      className={`border-b ${tone} px-4 lg:px-8 py-2 text-xs sm:text-sm flex items-center gap-3`}
    >
      <Warning size={16} weight="duotone" className="shrink-0" />
      <div className="flex-1 min-w-0">{label}</div>
      <button
        type="button"
        onClick={() => navigate("/billing/subscription")}
        className="inline-flex items-center gap-1 rounded-md bg-white/70 hover:bg-white px-2.5 py-1 text-xs font-semibold border border-current/20 whitespace-nowrap"
        data-testid="trial-banner-upgrade"
      >
        {isExpired && !isTrial ? "Renew now" : "Upgrade now"} <ArrowRight size={12} />
      </button>
      {!isExpired && (
        <button
          type="button"
          onClick={onDismiss}
          className="p-1 rounded hover:bg-white/60"
          aria-label="Dismiss"
          data-testid="trial-banner-dismiss"
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
}
